import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  AppWindow,
  Bot,
  Check,
  Clock,
  CreditCard,
  ExternalLink,
  Globe,
  Layers,
  Loader2,
  Lock,
  Mail,
  Plug,
  RefreshCw,
  ShieldCheck,
  Waypoints,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { McpServer, McpServerTool } from '../integrations/mcpServerRegistry';
import { useMcpServerDraft } from '../integrations/useMcpServerDraft';
import type { ConnectedAccount } from '../integrations/StripePayments';
import { ONBOARDING_EASE, Panel, SelectCard, WideSelectCard, onboardingInputClass } from './OnboardingStage';
import type { IntentKey, PaymentsChoice, SurfaceKey } from './types';
import { productNameFromUrl } from './types';
import { StartNowSection } from './StartNowSection';

/** Children fade/blur up one after another as the step enters. */
const staggerParent = { enter: {}, center: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } } };
const staggerChild = {
  enter: { opacity: 0, y: 12, filter: 'blur(4px)' },
  center: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.5, ease: ONBOARDING_EASE } },
};

function FeatureChips({ items }: { items: string[] }) {
  return (
    <span className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <span
          key={item}
          className="rounded-full border border-border/70 bg-background/80 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground"
        >
          {item}
        </span>
      ))}
    </span>
  );
}

// ---------------------------------------------------------------------------
// 1. Intent
// ---------------------------------------------------------------------------

export function IntentStep({
  intents,
  onToggle,
}: {
  intents: IntentKey[];
  onToggle: (key: IntentKey) => void;
}) {
  return (
    <>
      <motion.div variants={staggerParent} className="grid gap-4 sm:grid-cols-2">
        <motion.div variants={staggerChild} className="flex">
          <WideSelectCard
            selected={intents.includes('services')}
            onClick={() => onToggle('services')}
            icon={Layers}
            title="Deploy agentic services on top of my product"
            description="Aimdoc reads your MCP, builds the first services for you, and lets you charge for them."
          >
            <FeatureChips items={['Reads your MCP', 'Builds services', 'Stripe billing']} />
          </WideSelectCard>
        </motion.div>
        <motion.div variants={staggerChild} className="flex">
          <WideSelectCard
            selected={intents.includes('agent')}
            onClick={() => onToggle('agent')}
            icon={Bot}
            title="Deploy a customer agent"
            description="One agent that meets every customer on your website and inside your product, knows who they are, and does the work."
          >
            <FeatureChips items={['Website', 'In-app', 'Email']} />
          </WideSelectCard>
        </motion.div>
      </motion.div>
      <p className="mt-5 text-center text-[13px] text-muted-foreground">Pick one or both. You can add the other any time.</p>
    </>
  );
}

// ---------------------------------------------------------------------------
// 2. Connect your product (org-level MCP server)
// ---------------------------------------------------------------------------

type AuthChoice = 'oauth' | 'api_key';


export function ConnectProductStep({
  server,
  draftApi,
  oauthError,
  connecting = false,
}: {
  /** Already-connected server (resume / back navigation / OAuth return). */
  server: McpServer | null;
  draftApi: ReturnType<typeof useMcpServerDraft>;
  /** Message from a failed OAuth round-trip (`?mcp_oauth=error`). */
  oauthError: string | null;
  /** Back from sign-in, server row still loading. */
  connecting?: boolean;
}) {
  const { draft, setField, updateConnectionDetails, test, testing, testResult, testError, saveError } = draftApi;
  const discovered = server ? server.tools : testResult?.tools ?? null;
  const authChoice: AuthChoice = draft.auth_mode === 'api_key' ? 'api_key' : 'oauth';
  // The product's name comes from the MCP URL; nobody types it. It doubles
  // as the server row's name and as the label in every sentence below.
  const inferredName = productNameFromUrl(draft.url);
  const label = server?.name || inferredName || 'your product';
  const urlFilled = Boolean(inferredName);

  useEffect(() => {
    if (server && !draft.name) {
      setField({ name: server.name, url: server.url, auth_mode: server.auth_mode, description: server.description ?? '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server]);

  useEffect(() => {
    if (!server && draft.name !== inferredName) setField({ name: inferredName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inferredName, server]);

  // Kept to one short line each so the strip stays a single compact row.
  const whatHappens = [
    <>Sign in to {label}. Nothing to install.</>,
    <>Aimdoc reads the tools it exposes.</>,
    <>Your services and agent are built from them.</>,
  ];

  return (
    <>
      {/* Why this step matters, as a compact strip; the subtitle carries it on small screens. */}
      <motion.ol variants={staggerParent} className="mb-4 hidden grid-cols-3 gap-2 sm:grid">
        {whatHappens.map((line, index) => (
          <motion.li
            key={index}
            variants={staggerChild}
            className="flex items-start gap-2 rounded-xl border border-border/70 bg-card/70 px-3 py-2.5 text-[12px] leading-[18px] text-muted-foreground backdrop-blur"
          >
            <span className="mt-px shrink-0 font-mono text-[10px] font-medium leading-[18px] tracking-[0.1em] text-[#1173F0] dark:text-[#6AA8FF]">
              {String(index + 1).padStart(2, '0')}
            </span>
            <span>{line}</span>
          </motion.li>
        ))}
      </motion.ol>

      {connecting && (
        <Panel className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#1173F0]/10 text-[#1173F0] dark:text-[#6AA8FF]">
            <Loader2 className="h-4 w-4 animate-spin" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">Finishing sign-in</div>
            <div className="text-[13px] text-muted-foreground">Reading the tools your product exposes.</div>
          </div>
        </Panel>
      )}

      {!connecting && (
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={server ? 'connected' : 'form'}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.24, ease: ONBOARDING_EASE }}
          >
            {/* Once connected, the form leaves and the tool list takes its place. */}
            {!server && (
              <Panel className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="onboarding-mcp-url">MCP server URL</Label>
                  <Input
                    id="onboarding-mcp-url"
                    value={draft.url}
                    placeholder="https://mcp.yourcompany.com/mcp"
                    className={cn(onboardingInputClass, 'font-mono text-[13px]')}
                    onChange={(event) => updateConnectionDetails({ url: event.target.value })}
                  />
                  {inferredName && (
                    <p className="text-xs text-muted-foreground">
                      We will call this connection {inferredName}. You can rename it later in Integrations.
                    </p>
                  )}
                </div>

                {authChoice === 'api_key' && urlFilled && (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="onboarding-mcp-key">API key</Label>
                      <button
                        type="button"
                        onClick={() => updateConnectionDetails({ auth_mode: 'oauth', api_key: '' })}
                        className="text-xs font-medium text-[#1173F0] underline-offset-4 hover:underline dark:text-[#6AA8FF]"
                      >
                        Sign in instead
                      </button>
                    </div>
                    <div className="relative">
                      <Input
                        id="onboarding-mcp-key"
                        type="password"
                        value={draft.api_key}
                        placeholder="Your MCP server API key"
                        autoComplete="new-password"
                        className={cn(onboardingInputClass, 'pr-10')}
                        onChange={(event) => updateConnectionDetails({ api_key: event.target.value })}
                      />
                      <Lock className="pointer-events-none absolute right-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    </div>
                  </div>
                )}

                {urlFilled && authChoice === 'oauth' && (
                  <p className="text-[13px] leading-5 text-muted-foreground">
                    Continue sends you to {label} to sign in, then straight back here.{' '}
                    <button
                      type="button"
                      onClick={() => updateConnectionDetails({ auth_mode: 'api_key' })}
                      className="font-medium text-[#1173F0] underline-offset-4 hover:underline dark:text-[#6AA8FF]"
                    >
                      Use an API key instead
                    </button>
                  </p>
                )}

                {urlFilled && authChoice !== 'oauth' && (
                  <div className="flex items-center justify-between gap-3 pt-1">
                    <span className="text-xs text-muted-foreground">
                      Stored once for your organization. Every agent and service uses it.
                    </span>
                    <Button type="button" variant="outline" size="sm" className="h-9 shrink-0 gap-2 rounded-full px-4" disabled={testing} onClick={() => test()}>
                      {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plug className="h-[15px] w-[15px]" />}
                      {testing ? 'Testing...' : 'Test connection'}
                    </Button>
                  </div>
                )}
                {(oauthError || testError || saveError) && (
                  <p className="text-xs text-red-500">{oauthError ?? testError ?? saveError}</p>
                )}
              </Panel>
            )}

            {discovered && (
              <div className={server ? undefined : 'mt-4 animate-in fade-in slide-in-from-bottom-1 duration-300'}>
                <ToolsReveal label={label} tools={discovered} server={server} />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      )}

      {(server || urlFilled) && <StartNowSection mcpUrl={server?.url || draft.url} productName={label} />}

    </>
  );
}

/**
 * Connected/discovered state. The tool list is the motivation: these are what
 * services and the agent get built from. Capped at four cells (three tools
 * plus a "more" cell) so the step never grows past its budget.
 */
function ToolsReveal({ label, tools, server }: { label: string; tools: McpServerTool[]; server: McpServer | null }) {
  const shown = tools.slice(0, tools.length > 4 ? 3 : 4);
  const extra = tools.length - shown.length;
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_32px_-20px_rgba(16,24,40,0.18)]">
      <div className="flex items-center gap-3 px-4 py-3.5">
        <motion.span
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 500, damping: 26 }}
          className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white shadow-[0_6px_16px_-6px_rgba(16,185,129,0.7)]"
        >
          <span className="absolute inset-0 animate-ping rounded-full bg-emerald-500/30 [animation-iteration-count:2]" />
          <Check className="relative h-[18px] w-[18px]" strokeWidth={2.75} />
        </motion.span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">Connected to {label}</div>
          <div className="truncate text-xs text-muted-foreground">
            {tools.length === 0
              ? 'The server exposed no tools.'
              : `${tools.length} tool${tools.length === 1 ? '' : 's'} discovered`}
            {server?.auth_mode === 'oauth' && server.oauth?.connected_by_email && (
              <> · signed in as {server.oauth.connected_by_email}</>
            )}
          </div>
        </div>
      </div>
      {shown.length > 0 && (
        <div className="grid gap-px border-t border-border bg-border sm:grid-cols-2">
          {shown.map((tool, index) => (
            <motion.div
              key={tool.name}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 + index * 0.06, duration: 0.4, ease: ONBOARDING_EASE }}
              className="flex min-w-0 items-baseline gap-2 bg-card px-4 py-2.5"
            >
              <span className="shrink-0 rounded-md bg-muted/70 px-1.5 py-0.5 font-mono text-[11px] text-foreground">{tool.name}</span>
              {tool.description && <span className="truncate text-xs text-muted-foreground">{tool.description}</span>}
            </motion.div>
          ))}
          {extra > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.12 + shown.length * 0.06, duration: 0.4 }}
              className="flex items-center bg-card px-4 py-2.5 text-xs text-muted-foreground"
            >
              and {extra} more, all used to build your services and agent
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. Surfaces
// ---------------------------------------------------------------------------

const SURFACES: Array<{ key: SurfaceKey; title: string; description: string; icon: typeof Globe }> = [
  {
    key: 'website',
    title: 'Website',
    description: 'Meets prospects on your site, qualifies them, and gets them into the product.',
    icon: Globe,
  },
  {
    key: 'product',
    title: 'Application',
    description: 'Works inside your product, signed in as the customer, and completes tasks for them.',
    icon: AppWindow,
  },
  {
    key: 'email',
    title: 'Email',
    description: 'Reaches out when something needs to happen next. Customers just reply.',
    icon: Mail,
  },
  {
    key: 'gateway',
    title: 'Gateway',
    description: "Lets your customers' own AI agents find and use your services.",
    icon: Waypoints,
  },
];

export function SurfacesStep({
  surfaces,
  onToggle,
  hasMcp,
}: {
  surfaces: SurfaceKey[];
  onToggle: (key: SurfaceKey) => void;
  hasMcp: boolean;
}) {
  return (
    <motion.div variants={staggerParent} className="flex flex-col gap-3">
      {SURFACES.map(({ key, ...item }) => (
        <motion.div key={key} variants={staggerChild}>
          <SelectCard
            {...item}
            badge={key === 'product' && hasMcp ? 'Uses your MCP' : undefined}
            selected={surfaces.includes(key)}
            onClick={() => onToggle(key)}
          />
        </motion.div>
      ))}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// 4. Get paid
// ---------------------------------------------------------------------------

export function PaymentsStep({
  choice,
  onChoose,
}: {
  choice: PaymentsChoice;
  onChoose: (choice: PaymentsChoice) => void;
}) {
  return (
    <motion.div variants={staggerParent} className="grid gap-4 sm:grid-cols-2">
      <motion.div variants={staggerChild} className="flex">
      <WideSelectCard
        selected={choice === 'stripe'}
        onClick={() => onChoose('stripe')}
        icon={CreditCard}
        badge="Recommended"
        title="Set up payments with Stripe"
        description="Your first paid service can go live the moment it's built."
      >
        <span className="flex flex-col gap-2.5 text-[13px] leading-5">
          {[
            'Buyers pay you directly. You stay the merchant of record.',
            'Stripe verifies you in the background while we keep building.',
            'About 10 minutes. Aimdoc never sees your bank or identity details.',
          ].map((line) => (
            <span key={line} className="flex gap-2.5">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
              <span>{line}</span>
            </span>
          ))}
        </span>
      </WideSelectCard>
      </motion.div>
      <motion.div variants={staggerChild} className="flex">
      <WideSelectCard
        selected={choice === 'later'}
        onClick={() => onChoose('later')}
        icon={Clock}
        title="Not yet"
        description="Your services still run, but every one of them is free until Stripe is connected."
      >
        <span className="text-[13px] leading-5 text-muted-foreground">
          You can turn payments on later from Integrations. Services you built as free stay free until you price them.
        </span>
      </WideSelectCard>
      </motion.div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// 4a. Stripe handoff
// ---------------------------------------------------------------------------

export function StripeHandoffStep({ organizationName }: { organizationName: string }) {
  const steps = [
    ['Business details', 'Legal name, EIN, address'],
    ['Bank account', 'Where payouts land'],
    ['Verification', 'Runs in the background'],
  ];
  return (
    <Panel className="space-y-5">
      <div className="flex items-center gap-3.5">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#635BFF]/10 text-[#635BFF]">
          <ShieldCheck className="h-[22px] w-[22px]" strokeWidth={1.75} />
        </span>
        <div>
          <div className="text-[15px] font-semibold">Aimdoc creates a Stripe account for {organizationName}</div>
          <div className="mt-0.5 text-[13px] leading-5 text-muted-foreground">
            If you already use Stripe, this one is separate. It shows up next to your existing account in Stripe's
            account switcher, under the same login.
          </div>
        </div>
      </div>
      <div className="h-px bg-border" />
      <div className="space-y-3">
        <div className="font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">What Stripe will ask for</div>
        <div className="grid gap-3 sm:grid-cols-3">
          {steps.map(([title, detail], index) => (
            <div key={title} className="rounded-xl border border-border/80 bg-muted/20 p-3.5">
              <div className="font-mono text-[10px] font-medium tracking-[0.14em] text-[#635BFF]">{String(index + 1).padStart(2, '0')}</div>
              <div className="mt-1 text-sm font-medium">{title}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{detail}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2.5 text-[13px] text-muted-foreground">
        <Lock className="h-[15px] w-[15px]" />
        Handled on Stripe's hosted pages. You come straight back here when you're done.
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// 4b. Back from Stripe
// ---------------------------------------------------------------------------

const RETURN_COPY: Record<ConnectedAccount['status'], { title: string; badge: string; tone: string; body: string }> = {
  pending: {
    title: 'Stripe is verifying your details',
    badge: 'Verification pending',
    tone: 'text-amber-600 bg-amber-600/10',
    body:
      'Usually minutes, sometimes a day for a new business. Nothing is waiting on it: we keep building, and payments switch on by themselves once Stripe finishes. Paid services stay in draft until then.',
  },
  active: {
    title: 'Payments are ready',
    badge: 'Ready to accept payments',
    tone: 'text-green-700 bg-green-600/10',
    body: 'Stripe has verified your account. Paid services can go live as soon as they are built.',
  },
  restricted: {
    title: 'Stripe needs something from you',
    badge: 'Action required',
    tone: 'text-red-600 bg-red-600/10',
    body:
      'Stripe paused this account until you finish its requirements. Open the Stripe Dashboard to see what is missing. We keep building in the meantime.',
  },
  disconnected: {
    title: 'This Stripe account was disconnected',
    badge: 'Disconnected',
    tone: 'text-muted-foreground bg-muted',
    body: 'Contact Aimdoc to reconnect it. Your services still run as free ones until then.',
  },
};

export function StripeReturnedStep({
  account,
  syncing,
  onRefresh,
  onContinueOnboarding,
}: {
  account: ConnectedAccount | null;
  syncing: boolean;
  onRefresh: () => void;
  onContinueOnboarding: () => void;
}) {
  const status = account?.status ?? 'pending';
  const copy = RETURN_COPY[status];
  const incomplete = account && !account.details_submitted;
  return (
    <Panel className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <span
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
              status === 'active' ? 'bg-green-600/10 text-green-700' : 'bg-amber-600/10 text-amber-600'
            )}
          >
            {status === 'active' ? <Check className="h-[22px] w-[22px]" /> : <Clock className="h-[22px] w-[22px]" />}
          </span>
          <div>
            <div className="text-[15px] font-semibold">{incomplete ? 'Stripe onboarding is not finished' : copy.title}</div>
            {account && (
              <div className="mt-0.5 font-mono text-xs text-muted-foreground">
                {account.stripe_account_id}
                {account.country ? ` · ${account.country}` : ''}
                {account.default_currency ? ` · ${account.default_currency.toUpperCase()}` : ''}
              </div>
            )}
          </div>
        </div>
        <span className={cn('shrink-0 rounded-full px-2.5 py-1 text-xs font-medium', copy.tone)}>
          {incomplete ? 'Onboarding incomplete' : copy.badge}
        </span>
      </div>
      <div className="text-sm leading-[22px] text-muted-foreground">
        {incomplete
          ? 'You left Stripe before it had everything it needs. You can finish now or continue and come back later from Integrations. Services stay free until Stripe is done.'
          : copy.body}
      </div>
      <div className="flex flex-wrap gap-2.5">
        {incomplete && (
          <Button type="button" variant="outline" size="sm" className="h-9 gap-2 rounded-full px-4" onClick={onContinueOnboarding}>
            <ExternalLink className="h-[15px] w-[15px]" /> Finish Stripe onboarding
          </Button>
        )}
        {account?.dashboard_url && (
          <Button type="button" variant="outline" size="sm" className="h-9 gap-2 rounded-full px-4" asChild>
            <a href={account.dashboard_url} target="_blank" rel="noreferrer">
              <ExternalLink className="h-[15px] w-[15px]" /> Open Stripe Dashboard
            </a>
          </Button>
        )}
        <Button type="button" variant="outline" size="sm" className="h-9 gap-2 rounded-full px-4" disabled={syncing} onClick={onRefresh}>
          {syncing ? <Loader2 className="h-[15px] w-[15px] animate-spin" /> : <RefreshCw className="h-[15px] w-[15px]" />}
          Refresh status
        </Button>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// 5. Website
// ---------------------------------------------------------------------------

/** Swaps the generic globe for the site's own favicon once the URL parses. */
function WebsiteFavicon({ value }: { value: string }) {
  const [failed, setFailed] = useState(false);
  let host = '';
  try {
    host = new URL(value.includes('://') ? value : `https://${value}`).hostname;
  } catch {
    host = '';
  }
  useEffect(() => setFailed(false), [host]);
  if (!host.includes('.') || failed) {
    return <Globe className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />;
  }
  return (
    <img
      src={`https://${host}/favicon.ico`}
      alt=""
      onError={() => setFailed(true)}
      className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 rounded-sm"
    />
  );
}

export function WebsiteStep({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <Panel className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="onboarding-website">Company website</Label>
        <div className="relative">
          <WebsiteFavicon value={value} />
          <Input
            id="onboarding-website"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            className={cn(onboardingInputClass, 'pl-10')}
            placeholder="https://example.com"
            autoFocus
          />
        </div>
      </div>
      <p className="text-[13px] leading-5 text-muted-foreground">
        We guessed this from your email. Aimdoc reads it to write your agent's persona and to name and describe your
        services in your own words.
      </p>
    </Panel>
  );
}
